import { supabase } from './supabase'
import { callClaude } from './claude-client'
import {
  MEDICAL_DOCUMENTS_BUCKET,
  createSignedMedicalDocumentUrl,
  filterVisibleMedicalDocuments,
  isMedicalDocumentStoragePath,
  removeMedicalDocumentFromPrivateBucket,
  resolveMedicalDocumentUrls,
  uploadMedicalDocumentToPrivateBucket
} from './medical-document-storage'
import { analyzeLocalMedicalDocument } from './claude-analysis'
import type { MedicalDocument, MedicalDocumentInput, LabResult, DocumentType } from '@/types/health'

// ================================
// FONCTIONS CRUD POUR LES DOCUMENTS
// ================================

const MEDICAL_DOCUMENTS_PAGE_SIZE = 1000

function buildMedicalDocumentsQuery(
  patientId: string,
  documentType?: DocumentType
) {
  let query = supabase
    .from('medical_documents')
    .select('*')
    .eq('patient_id', patientId)
    .order('document_date', { ascending: false })

  if (documentType) {
    query = query.eq('document_type', documentType)
  }

  return query
}

export async function createMedicalDocument(document: MedicalDocumentInput): Promise<MedicalDocument> {
  const { data, error } = await supabase
    .from('medical_documents')
    .insert(document)
    .select()
    .single()

  if (error) {
    throw new Error(`Erreur lors de la création du document: ${error.message}`)
  }

  const [resolvedDocument] = await resolveMedicalDocumentUrls([data])
  return resolvedDocument
}

export async function getMedicalDocuments(
  patientId: string,
  documentType?: DocumentType,
  limit: number | null = 50
): Promise<MedicalDocument[]> {
  const normalizedLimit =
    typeof limit === 'number' && Number.isFinite(limit)
      ? Math.max(0, Math.floor(limit))
      : null

  if (normalizedLimit === 0) {
    return []
  }

  const shouldFetchAll = normalizedLimit === null
  const documents: MedicalDocument[] = []
  let fetchedCount = 0
  let remaining = normalizedLimit ?? Number.POSITIVE_INFINITY

  while (remaining > 0) {
    const batchSize = shouldFetchAll
      ? MEDICAL_DOCUMENTS_PAGE_SIZE
      : Math.min(MEDICAL_DOCUMENTS_PAGE_SIZE, remaining)

    const { data, error } = await buildMedicalDocumentsQuery(patientId, documentType)
      .range(fetchedCount, fetchedCount + batchSize - 1)

    if (error) {
      throw new Error(`Erreur lors de la récupération des documents: ${error.message}`)
    }

    const nextBatch = (data || []) as MedicalDocument[]
    documents.push(...nextBatch)

    if (nextBatch.length < batchSize) {
      break
    }

    fetchedCount += nextBatch.length

    if (!shouldFetchAll) {
      remaining -= nextBatch.length
    }
  }

  return resolveMedicalDocumentUrls(filterVisibleMedicalDocuments(documents))
}

export async function getMedicalDocumentsCount(patientId: string): Promise<number> {
  const { count, error } = await supabase
    .from('medical_documents')
    .select('id', { count: 'exact', head: true })
    .eq('patient_id', patientId)

  if (error) {
    throw new Error(`Erreur lors du comptage des documents: ${error.message}`)
  }

  return count || 0
}

export async function updateMedicalDocument(
  documentId: string,
  updates: Partial<MedicalDocumentInput>
): Promise<MedicalDocument> {
  const { data, error } = await supabase
    .from('medical_documents')
    .update(updates)
    .eq('id', documentId)
    .select()
    .single()

  if (error) {
    throw new Error(`Erreur lors de la mise à jour du document: ${error.message}`)
  }

  const [resolvedDocument] = await resolveMedicalDocumentUrls([data])
  return resolvedDocument
}

export async function deleteMedicalDocument(documentId: string): Promise<void> {
  const { data: existingDocument, error: fetchError } = await supabase
    .from('medical_documents')
    .select('file_url')
    .eq('id', documentId)
    .maybeSingle()

  if (fetchError) {
    throw new Error(`Erreur lors de la recuperation du document: ${fetchError.message}`)
  }

  const { error } = await supabase
    .from('medical_documents')
    .delete()
    .eq('id', documentId)

  if (error) {
    throw new Error(`Erreur lors de la suppression du document: ${error.message}`)
  }

  if (isMedicalDocumentStoragePath(existingDocument?.file_url)) {
    await removeMedicalDocumentFromPrivateBucket(existingDocument.file_url)
  }
}

export async function searchMedicalDocuments(
  patientId: string,
  searchTerm: string,
  filters?: {
    documentType?: DocumentType
    dateFrom?: string
    dateTo?: string
    practitioner?: string
    specialty?: string
  }
): Promise<MedicalDocument[]> {
  let query = supabase
    .from('medical_documents')
    .select('*')
    .eq('patient_id', patientId)
    .order('document_date', { ascending: false })

  // Recherche textuelle
  if (searchTerm) {
    query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,practitioner_name.ilike.%${searchTerm}%`)
  }

  // Filtres
  if (filters) {
    if (filters.documentType) {
      query = query.eq('document_type', filters.documentType)
    }
    if (filters.dateFrom) {
      query = query.gte('document_date', filters.dateFrom)
    }
    if (filters.dateTo) {
      query = query.lte('document_date', filters.dateTo)
    }
    if (filters.practitioner) {
      query = query.ilike('practitioner_name', `%${filters.practitioner}%`)
    }
    if (filters.specialty) {
      query = query.ilike('specialty', `%${filters.specialty}%`)
    }
  }

  const { data, error } = await query

  if (error) {
    throw new Error(`Erreur lors de la recherche: ${error.message}`)
  }

  return resolveMedicalDocumentUrls(filterVisibleMedicalDocuments(data || []))
}

// ================================
// GESTION DES FICHIERS (UPLOAD)
// ================================

export async function uploadDocumentFile(
  file: File,
  patientId: string,
  documentId?: string
): Promise<{ url: string; path: string; fileName: string; storagePath: string }> {
  if (file.type !== 'application/pdf') {
    throw new Error('Seuls les fichiers PDF sont acceptes')
  }

  const uploadedFile = await uploadMedicalDocumentToPrivateBucket(file, patientId)
  const signedUrl = uploadedFile.signedUrl || await createSignedMedicalDocumentUrl(uploadedFile.storagePath)

  return {
    url: signedUrl,
    path: uploadedFile.storagePath,
    fileName: documentId ? `${documentId}:${uploadedFile.originalFileName}` : uploadedFile.originalFileName,
    storagePath: uploadedFile.storagePath
  }

  if (file.type !== 'application/pdf') {
    throw new Error('Seuls les fichiers PDF sont acceptés')
  }

  const fileExt = file.name.split('.').pop()
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  const fileName = `${patientId}/${documentId || 'doc'}_${timestamp}.${fileExt}`

  // Upload vers le bucket medical-documents
  const { data, error: uploadError } = await supabase.storage
    .from('medical-documents')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false
    })

  if (uploadError) {
    throw new Error(`Erreur lors de l'upload: ${uploadError.message}`)
  }

  // Générer une URL signée (valide 1 an)
  const { data: urlData } = await supabase.storage
    .from('medical-documents')
    .createSignedUrl(fileName, 31536000) // 1 an en secondes

  return {
    url: urlData?.signedUrl || '',
    path: data.path,
    fileName: data.path,
    storagePath: data.path
  }
}

export async function deleteDocumentFile(filePath: string): Promise<void> {
  await removeMedicalDocumentFromPrivateBucket(filePath)
  return

  const { error } = await supabase.storage
    .from('medical-documents')
    .remove([filePath])

  if (error) {
    console.warn('Erreur lors de la suppression du fichier:', error.message)
  }
}

// Télécharger un document
export async function downloadMedicalFile(fileName: string): Promise<Blob> {
  const { data, error } = await supabase.storage
    .from(MEDICAL_DOCUMENTS_BUCKET)
    .download(fileName)

  if (error) {
    throw new Error(`Erreur lors du téléchargement: ${error.message}`)
  }

  return data
}

// ================================
// INTÉGRATION AVEC LES RÉSULTATS D'ANALYSES
// ================================

// Créer des résultats d'analyses à partir de l'extraction
export async function createLabResults(
  results: Array<{
    test_name: string
    result_value: number
    unit: string
    reference_min?: number | null
    reference_max?: number | null
    is_abnormal?: boolean
    test_date: string
    patient_id: string
  }>
): Promise<any[]> {
  if (results.length === 0) {
    return []
  }

  const { data, error } = await supabase
    .from('lab_results')
    .insert(results)
    .select()

  if (error) {
    throw new Error(`Erreur lors de la sauvegarde des résultats: ${error.message}`)
  }

  return data || []
}

// Récupérer les résultats d'analyses liés à un document
export async function getLabResults(
  patientId: string,
  documentId?: string,
  limit = 100
): Promise<any[]> {
  let query = supabase
    .from('lab_results')
    .select('*')
    .eq('patient_id', patientId)
    .order('test_date', { ascending: false })
    .limit(limit)

  // Note : il faudrait ajouter document_id à la table lab_results pour cette fonctionnalité
  // Pour l'instant, on récupère tous les résultats du patient

  const { data, error } = await query

  if (error) {
    throw new Error(`Erreur lors de la récupération des résultats: ${error.message}`)
  }

  return data || []
}

// ================================
// EXTRACTION DE DONNÉES IA VIA CLAUDE API
// ================================

export async function extractDataFromDocument(
  file: File,
  documentType: DocumentType = 'consultation_report'
): Promise<{
  title?: string;
  document_type?: DocumentType;
  document_date?: string;
  doctor_name?: string;
  specialty?: string;
  content_summary?: string;
  key_findings?: string[];
  recommendations?: string[];
  urgency_level?: 'normal' | 'medium' | 'high';
  extracted_data?: any;
  lab_results?: any[];
  summary?: string;
}> {
  try {
    const analysis = await analyzeLocalMedicalDocument(file)
    const hasCriticalSignal =
      analysis.analyses.some((entry) => entry.statut === 'anormal' || entry.statut === 'critique') ||
      analysis.diagnostics.some((entry) => entry.gravite === 'severe' || entry.gravite === 'critique')

    return {
      title: file.name.replace('.pdf', ''),
      document_type: analysis.type_document || documentType,
      document_date: analysis.date_document || new Date().toISOString().split('T')[0],
      doctor_name: analysis.medecin,
      specialty: null,
      content_summary: analysis.resume || 'Document medical analyse automatiquement',
      key_findings: analysis.diagnostics.map((entry) => entry.diagnostic),
      recommendations: analysis.recommandations ? [analysis.recommandations] : [],
      urgency_level: hasCriticalSignal ? 'high' : 'normal',
      extracted_data: analysis as unknown as Record<string, unknown>,
      lab_results: analysis.analyses.map((entry) => {
        const numericValue = entry.valeur ? Number.parseFloat(entry.valeur.replace(',', '.')) : Number.NaN
        const referenceRangeMatch = entry.valeur_reference?.match(/(-?\d+(?:[.,]\d+)?)\s*[-a]\s*(-?\d+(?:[.,]\d+)?)/i)

        return {
          test_name: entry.nom_analyse,
          result_value: Number.isFinite(numericValue) ? numericValue : null,
          unit: entry.unite || null,
          reference_min: referenceRangeMatch ? Number.parseFloat(referenceRangeMatch[1].replace(',', '.')) : null,
          reference_max: referenceRangeMatch ? Number.parseFloat(referenceRangeMatch[2].replace(',', '.')) : null,
          is_abnormal: entry.statut === 'anormal' || entry.statut === 'critique',
          test_date: analysis.date_prelevement || analysis.date_document || new Date().toISOString().split('T')[0]
        }
      }),
      summary: analysis.resume || 'Analyse automatique realisee avec succes'
    }

    console.log('🧠 Extraction de données du document avec Claude IA...')

    // Lire le contenu du fichier
    const text = await readFileAsText(file)

    // Prompt spécialisé pour l'extraction de documents médicaux
    const extractionPrompt = `Analysez ce document médical et extrayez les informations importantes.

CONTENU DU DOCUMENT:
${text}

Répondez au format JSON strict suivant :
{
  "title": "Titre du document ou nom du type d'examen",
  "document_type": "consultation|prescription|blood_test|imaging|surgery_report|discharge_summary",
  "document_date": "Date du document au format YYYY-MM-DD",
  "doctor_name": "Nom du médecin si mentionné",
  "specialty": "Spécialité médicale",
  "content_summary": "Résumé en 2-3 phrases du contenu principal",
  "key_findings": ["Liste des findings principaux"],
  "recommendations": ["Liste des recommandations médicales"],
  "urgency_level": "normal|medium|high selon l'urgence",
  "patient_data": {
    "name": "Nom du patient si mentionné",
    "birth_date": "Date de naissance si mentionnée",
    "age": "Âge si mentionné"
  },
  "lab_results": [
    {
      "test_name": "Nom du test",
      "result_value": 12.5,
      "unit": "mg/L",
      "reference_min": 0,
      "reference_max": 3.0,
      "is_abnormal": true,
      "interpretation": "Élevé/Normal/Bas"
    }
  ]
}`

    // Utiliser le client Claude robuste
    const response = await callClaude(
      extractionPrompt,
      'Vous êtes un expert médical spécialisé dans l\'extraction de données à partir de documents médicaux. Analysez précisément et extrayez les informations importantes tout en respectant la confidentialité médicale.',
      3000
    )

    try {
      const parsedData = JSON.parse(response)

      // Transformer les résultats de laboratoire au format attendu
      let labResults: any[] = []
      if (parsedData.lab_results && Array.isArray(parsedData.lab_results)) {
        labResults = parsedData.lab_results.map((test: any) => ({
          test_name: test.test_name || test.nom_test,
          result_value: parseFloat(test.result_value || test.valeur) || null,
          unit: test.unit || test.unite,
          reference_min: parseFloat(test.reference_min || test.valeur_normale_min) || null,
          reference_max: parseFloat(test.reference_max || test.valeur_normale_max) || null,
          is_abnormal: Boolean(test.is_abnormal || test.anomalie),
          test_date: parsedData.document_date || new Date().toISOString().split('T')[0]
        }))
      }

      console.log('✅ Extraction réussie:', parsedData.title || 'Document analysé')

      return {
        title: parsedData.title || file.name.replace('.pdf', ''),
        document_type: parsedData.document_type || documentType,
        document_date: parsedData.document_date || new Date().toISOString().split('T')[0],
        doctor_name: parsedData.doctor_name,
        specialty: parsedData.specialty,
        content_summary: parsedData.content_summary || 'Document médical analysé automatiquement',
        key_findings: Array.isArray(parsedData.key_findings) ? parsedData.key_findings : [],
        recommendations: Array.isArray(parsedData.recommendations) ? parsedData.recommendations : [],
        urgency_level: parsedData.urgency_level || 'normal',
        extracted_data: parsedData,
        lab_results: labResults,
        summary: parsedData.content_summary || 'Analyse automatique réalisée avec succès'
      }

    } catch (parseError) {
      console.log('⚠️ Réponse non-JSON de Claude, traitement alternatif...')

      // Si le parsing JSON échoue, retourner une structure basique
      return {
        title: file.name.replace('.pdf', ''),
        document_type: documentType,
        document_date: new Date().toISOString().split('T')[0],
        content_summary: response.substring(0, 200) + '...',
        key_findings: [],
        recommendations: [],
        urgency_level: 'normal',
        summary: 'Document analysé - format de réponse inattendu'
      }
    }

  } catch (error: any) {
    console.error('❌ Erreur lors de l\'extraction de document:', error)

    // Retourner une structure par défaut en cas d'erreur
    return {
      title: file.name.replace('.pdf', ''),
      document_type: documentType,
      document_date: new Date().toISOString().split('T')[0],
      content_summary: 'Extraction automatique temporairement indisponible',
      key_findings: [],
      recommendations: ['Vérification manuelle recommandée'],
      urgency_level: 'normal',
      summary: 'Document téléchargé - extraction en cours de configuration'
    }
  }
}

// ================================
// FONCTIONS UTILITAIRES
// ================================

async function readFileAsText(file: File): Promise<string> {
  if (file.type === 'application/pdf') {
    return `Document PDF: ${file.name}\nTaille: ${(file.size / 1024).toFixed(1)} KB\nDate: ${new Date().toLocaleDateString()}\nType: Document médical\n\n[Contenu du PDF analysé par l'IA]`
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target?.result as string || '')
    reader.onerror = () => reject(new Error('Erreur lecture fichier'))
    reader.readAsText(file)
  })
}

function extractBloodTestResults(text: string): any[] {
  const results: any[] = []

  // Patterns courants pour les analyses de sang
  const patterns = [
    // Hémoglobine: 12.5 g/dl (Normal: 12-16)
    /(\w+(?:\s+\w+)*)\s*:?\s*([0-9]+(?:\.[0-9]+)?)\s*([a-zA-Z\/]+)?\s*(?:\(.*?([0-9]+-[0-9]+).*?\))?/g,
    // TSH 2.3 mUI/L N: 0.4-4.0
    /(\w+(?:\s+\w+)*)\s+([0-9]+(?:\.[0-9]+)?)\s*([a-zA-Z\/]+)\s*N?:?\s*([0-9]+(?:\.[0-9]+)?-[0-9]+(?:\.[0-9]+)?)/g
  ]

  patterns.forEach(pattern => {
    let match
    while ((match = pattern.exec(text)) !== null) {
      const [, testName, value, unit, reference] = match

      if (testName && value) {
        results.push({
          test_name: testName.trim(),
          result_value: parseFloat(value),
          unit: unit || null,
          reference_range: reference || null,
          is_abnormal: false, // À calculer selon les références
          test_date: new Date().toISOString().split('T')[0] // À extraire du document
        })
      }
    }
  })

  return results
}

function extractRadiologyFindings(text: string): any {
  // Extraction simple des conclusions radiologiques
  const findingsPatterns = [
    /conclusion\s*:?\s*(.*?)(?:\n\n|\n[A-Z]|$)/is,
    /impression\s*:?\s*(.*?)(?:\n\n|\n[A-Z]|$)/is,
    /résultats?\s*:?\s*(.*?)(?:\n\n|\n[A-Z]|$)/is
  ]

  for (const pattern of findingsPatterns) {
    const match = text.match(pattern)
    if (match) {
      return {
        conclusion: match[1].trim(),
        extraction_confidence: 'medium'
      }
    }
  }

  return {
    raw_text: text.substring(0, 1000),
    extraction_confidence: 'low',
    manual_review_needed: true
  }
}

function extractMedicationsFromPrescription(text: string): any[] {
  const medications: any[] = []

  // Patterns pour les prescriptions
  const medicationPatterns = [
    // MEDICAMENT 500mg - 2 fois par jour
    /([A-Z][a-zA-Z\s]+)\s+([0-9]+\s*mg)\s*-?\s*(.*?)(?:\n|$)/g,
    // Medicament: dosage, posologie
    /(\w+(?:\s+\w+)*)\s*:?\s*([0-9]+\s*mg[^,\n]*),?\s*([^,\n]*)/g
  ]

  medicationPatterns.forEach(pattern => {
    let match
    while ((match = pattern.exec(text)) !== null) {
      const [, name, dosage, frequency] = match

      if (name && name.length > 2) {
        medications.push({
          name: name.trim(),
          dosage: dosage?.trim() || null,
          frequency: frequency?.trim() || null
        })
      }
    }
  })

  return medications
}

export function getDocumentTypeLabel(type: DocumentType): string {
  const labels: Record<DocumentType, string> = {
    blood_test: 'Analyse de sang',
    radiology: 'Imagerie médicale',
    prescription: 'Ordonnance',
    consultation_report: 'Compte-rendu consultation',
    discharge_summary: 'Compte-rendu hospitalisation',
    pathology: 'Anatomopathologie',
    cardiology: 'Cardiologie',
    specialist_report: 'Rapport de spécialiste',
    vaccination_record: 'Carnet de vaccination',
    insurance_document: 'Document assurance',
    other: 'Autre document'
  }
  return labels[type] || type
}

export function getDocumentTypeColor(type: DocumentType): string {
  const colors: Record<DocumentType, string> = {
    blood_test: 'text-red-400 bg-red-400/10 border-red-400/20',
    radiology: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    prescription: 'text-green-400 bg-green-400/10 border-green-400/20',
    consultation_report: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
    discharge_summary: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
    pathology: 'text-pink-400 bg-pink-400/10 border-pink-400/20',
    cardiology: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
    specialist_report: 'text-indigo-400 bg-indigo-400/10 border-indigo-400/20',
    vaccination_record: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    insurance_document: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
    other: 'text-gray-400 bg-gray-400/10 border-gray-400/20'
  }
  return colors[type] || colors.other
}

export function getDocumentIcon(type: DocumentType): string {
  const icons: Record<DocumentType, string> = {
    blood_test: '🩸',
    radiology: '📷',
    prescription: '💊',
    consultation_report: '📋',
    discharge_summary: '🏥',
    pathology: '🔬',
    cardiology: '❤️',
    specialist_report: '👨‍⚕️',
    vaccination_record: '💉',
    insurance_document: '📄',
    other: '📄'
  }
  return icons[type] || icons.other
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export function validateDocumentInput(input: MedicalDocumentInput): { isValid: boolean; errors: string[] } {
  const errors: string[] = []

  if (!input.title?.trim()) {
    errors.push('Le titre du document est obligatoire')
  }

  if (!input.document_type) {
    errors.push('Le type de document est obligatoire')
  }

  if (!input.document_date) {
    errors.push('La date du document est obligatoire')
  }

  if (input.document_date && new Date(input.document_date) > new Date()) {
    errors.push('La date du document ne peut pas être dans le futur')
  }

  if (!input.file_name?.trim()) {
    errors.push('Le nom du fichier est obligatoire')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}
